console.log("I'm here");

import Chord from './chord';
import type MusicObject from './musicObject';
import Note from './note';
import NoteHolder from './noteHolder';
import type { MidiMessage, PlayMusicOptions, PlayOptions } from './soundBufferHolder';

export abstract class AbstractInstrument {
  public abstract noteOn(midiNumber: number | Note, velocity?: number): AbstractInstrument;

  public abstract noteOff(midiNumber: number | Note | Chord | NoteHolder): AbstractInstrument;

  public abstract allNotesOff(): AbstractInstrument;

  public abstract play(
    toPlay: number | Note | Chord | NoteHolder,
    playOptions?: PlayOptions
  ): AbstractInstrument;

  public playMusic(
    musicObject: MusicObject,
    playOptions: PlayMusicOptions = {}
  ): AbstractInstrument {
    const { tempo, startTime } = playOptions;
    const midiMessages = musicObject.getMidiMessages();
    this.playMidiMessages(midiMessages, tempo || musicObject.tempo, startTime);
    return this;
  }

  public abstract playMidiMessages(
    midiMessages: MidiMessage[],
    tempo?: number,
    startTime?: number
  ): AbstractInstrument;

  // eslint-disable-next-line class-methods-use-this
  protected getRelativeTimes(playOptions: PlayOptions = {}, now?: number) {
    const { startTime = 0, stopTime, absoluteTiming } = playOptions;
    if (absoluteTiming) {
      return {
        startTime: startTime - (now ?? 0),
        stopTime: typeof stopTime === 'undefined' ? stopTime : stopTime - (now ?? 0),
      };
    }
    return {
      startTime,
      stopTime,
    };
  }
}

/**
 * Converts a variety of types into an array of midi numbers or strings.
 */
export function anythingToMidiNumbers<T extends number | string | Note | Chord | NoteHolder>(
  toConvert: T
): T extends string ? string[] : number[] {
  if (typeof toConvert === 'number' || typeof toConvert === 'string') {
    return [toConvert] as T extends string ? string[] : number[]; // we didn't convert
  }

  if (toConvert instanceof Note) {
    const midiNumber = toConvert.getMidiNumber();
    return [midiNumber] as T extends string ? string[] : number[];
  }

  const noteHolder =
    // eslint-disable-next-line no-nested-ternary
    toConvert instanceof Chord
      ? toConvert.noteHolder
      : toConvert instanceof NoteHolder
      ? toConvert
      : undefined;

  if (noteHolder) {
    const midiNumberArray: number[] = [];
    noteHolder.forEach((note) => {
      const midiNumber = note.getMidiNumber();
      midiNumberArray.push(midiNumber);
    });
    return midiNumberArray as T extends string ? string[] : number[];
  }

  // guarantee return
  return [] as unknown as T extends string ? string[] : number[];
}

export function beatToTimeMS(beat: number, tempo: number = 100, startTime: number = 0) {
  return beat * (60 / tempo) + startTime;
}
